import { useCallback, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import type { PlaylistItemResolved } from 'src/features/AddToPlaylist/api';
import {
  usePlaylistContentSearch,
  usePlaylistMutations,
  type PlaylistSearchOption,
} from 'src/features/AddToPlaylist';

export function PlaylistAddItemSearch({
  playlistId,
  existingItems,
}: {
  playlistId: string;
  existingItems: PlaylistItemResolved[];
}) {
  const [inputValue, setInputValue] = useState('');
  const { options, isLoading } = usePlaylistContentSearch(inputValue);
  const { addItem } = usePlaylistMutations();
  const { enqueueSnackbar } = useSnackbar();

  const existingKeys = useMemo(
    () =>
      new Set(
        existingItems.map((item) => `${item.contentType}:${item.contentId}`),
      ),
    [existingItems],
  );

  const availableOptions = useMemo(
    () => options.filter((option) => !existingKeys.has(`${option.contentType}:${option.contentId}`)),
    [options, existingKeys],
  );

  const handleSelect = useCallback(
    async (_: unknown, option: PlaylistSearchOption | null) => {
      if (!option) return;
      try {
        await addItem.mutateAsync({
          playlistId,
          contentId: option.contentId,
          contentType: option.contentType,
        });
        enqueueSnackbar('Dodano do playlisty', { variant: 'success' });
        setInputValue('');
      } catch {
        enqueueSnackbar('Nie udało się dodać do playlisty', { variant: 'error' });
      }
    },
    [addItem, enqueueSnackbar, playlistId],
  );

  return (
    <Autocomplete
      sx={{ mb: 2, width: 1 }}
      fullWidth
      options={availableOptions}
      loading={isLoading}
      inputValue={inputValue}
      onInputChange={(_, value) => setInputValue(value)}
      onChange={handleSelect}
      value={null}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.title}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      noOptionsText={
        inputValue.trim().length < 2
          ? 'Wpisz co najmniej 2 znaki'
          : 'Brak wyników'
      }
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={{
            display: 'grid !important',
            gridTemplateColumns: '72px 1fr',
            columnGap: 1.5,
            alignItems: 'center',
          }}
        >
          {option.thumbnailUrl ? (
            <Box
              component="img"
              src={option.thumbnailUrl}
              alt=""
              sx={{ width: 72, height: 40, objectFit: 'cover', borderRadius: 0.5 }}
            />
          ) : (
            <Box
              sx={{
                width: 72,
                height: 40,
                bgcolor: 'action.hover',
                borderRadius: 0.5,
              }}
            />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {option.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {option.typeLabel}
              {option.subtitle ? ` · ${option.subtitle}` : ''}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          hiddenLabel
          placeholder="Wyszukaj sesję, którą chcesz dodać do playlisty"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      disabled={addItem.isLoading}
    />
  );
}
